import React, { useEffect, useMemo, useState } from 'react';
import { Collapse } from 'react-collapse';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

function ActivityLogEntry({ command, responses }) {
  const [isOpen, setIsOpen] = useState(false);

  // command: { command: string, timestamp: number }
  const sentTime = command?.timestamp;

  // responses: { [key: string]: { response: string, response_id: string, timestamp: number } }
  const responseList = useMemo(() => {
    if (!responses || typeof responses !== 'object') return [];
    return Object.values(responses).filter(Boolean);
  }, [responses]);

  const lastResponseTime = useMemo(() => {
    if (responseList.length === 0) return null;
    return Math.max(...responseList.map((r) => r.timestamp || 0));
  }, [responseList]);

  return (
    <div className="border border-secondary rounded-lg p-4 mb-4">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div>
          <h3 className="text-primary font-bold">{command?.command ?? '—'}</h3>
          <p className="text-secondary">
            Sent:{' '}
            {sentTime ? new Date(sentTime * 1000).toLocaleString() : 'Unknown'}{' '}
            | Last Response:{' '}
            {lastResponseTime
              ? new Date(lastResponseTime * 1000).toLocaleString()
              : 'No responses'}
          </p>
        </div>

        <svg
          className={`w-6 h-6 transition-transform transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      <Collapse isOpened={isOpen}>
        <div className="bg-background rounded-lg mt-2">
          <h4 className="text-accent font-semibold">Responses:</h4>

          {responseList.length === 0 ? (
            <p className="text-secondary">No responses</p>
          ) : (
            <ul className="list-disc list-inside">
              {responseList
                .slice()
                .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
                .map((r) => (
                  <li key={r.response_id} className="text-secondary">
                    <span className="font-bold">{r.response_id}</span> —{' '}
                    {r.response}
                    {r.timestamp ? (
                      <span className="ml-2 opacity-70">
                        ({new Date(r.timestamp * 1000).toLocaleString()})
                      </span>
                    ) : null}
                  </li>
                ))}
            </ul>
          )}
        </div>
      </Collapse>
    </div>
  );
}

function escapeCsv(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function formatTimestamp(ts) {
  if (!ts) return '';
  return new Date(ts * 1000).toISOString();
}

export default function ActivityLog({ serialNumber }) {
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const pageSizeOptions = [10, 25, 50, 100];

  const SERVER_PAGE_SIZE = 50;
  const { sendAuthenticatedRequest } = useAuth();

  async function loadActivityLogs(cursor = null) {
    if (cursor !== null) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page_size: SERVER_PAGE_SIZE.toString(),
        last_timestamp: cursor ? cursor.toString() : '-1',
      });

      const res = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/logs/activity?${params.toString()}`
      );

      const raw = res || {};
      const itemsObj = raw.items ?? raw;

      const newLogs = Object.entries(itemsObj || {}).map(
        ([logId, logData]) => ({
          logId,
          ...logData,
        })
      );

      setNextCursor(raw.next_cursor ?? null);

      setActivityLogs((prev) => {
        const existingIds = new Set(prev.map((l) => l.logId));
        const merged = [...prev];
        newLogs.forEach((log) => {
          if (!existingIds.has(log.logId)) {
            merged.push(log);
          }
        });
        return merged.sort(
          (a, b) => (b.command?.timestamp || 0) - (a.command?.timestamp || 0)
        );
      });
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch activity logs');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }

  async function handleDownloadCsv() {
    setIsDownloadingCsv(true);
    try {
      const res = await sendAuthenticatedRequest(
        `/devices/${serialNumber}/logs/activity?max_entries=all`
      );

      const raw = res || {};
      const itemsObj = raw.items ?? raw;
      const logs = Object.values(itemsObj || {});

      if (!logs.length) {
        toast.error('No logs to download');
        return;
      }

      logs.sort(
        (a, b) => (b.command?.timestamp || 0) - (a.command?.timestamp || 0)
      );

      const responseKeySet = new Set();
      logs.forEach((log) => {
        const responses = log.responses?.responses || {};
        Object.keys(responses).forEach((k) => responseKeySet.add(k));
      });
      const responseKeys = Array.from(responseKeySet);

      const headers = [
        'command_timestamp',
        'command',
        'response_timestamp',
        ...responseKeys,
      ];

      const lines = [];
      lines.push(headers.map(escapeCsv).join(','));

      logs.forEach((log) => {
        const command = log.command || {};
        const responsesInfo = log.responses || {};
        const responses = responsesInfo.responses || {};

        const row = [
          formatTimestamp(command.timestamp),
          command.command ?? '',
          formatTimestamp(responsesInfo.timestamp),
          ...responseKeys.map((key) => responses[key] ?? ''),
        ];

        lines.push(row.map(escapeCsv).join(','));
      });

      const csvString = lines.join('\n');
      const blob = new Blob([csvString], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ActivityLog_${serialNumber}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error('Failed to download CSV');
    } finally {
      setIsDownloadingCsv(false);
    }
  }

  useEffect(() => {
    setActivityLogs([]);
    setNextCursor(null);
    setPage(1);
    loadActivityLogs(null);
  }, [serialNumber]);

  const totalRows = activityLogs.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const pagedLogs = useMemo(() => {
    const start = (page - 1) * pageSize;
    return activityLogs.slice(start, start + pageSize);
  }, [activityLogs, page, pageSize]);

  const startRow = totalRows ? (page - 1) * pageSize + 1 : 0;
  const endRow = Math.min(totalRows, page * pageSize);

  const showEndOfLog =
    !isLoading &&
    !isLoadingMore &&
    totalRows > 0 &&
    page === totalPages &&
    !nextCursor;

  return (
    <div className="space-y-4">
      {isLoading && totalRows === 0 && <p>Loading logs...</p>}

      {!isLoading && totalRows === 0 && (
        <p className="text-error font-bold">No logs</p>
      )}

      {pagedLogs.map((log) => (
        <ActivityLogEntry
          key={log.logId}
          command={log.command}
          responses={log.responses}
        />
      ))}

      {!isLoading && totalRows > 0 && (
        <div className="flex justify-between items-center mt-3">
          <div className="flex gap-2 items-center">
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              «
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              ‹
            </button>
            <span className="px-2 text-sm">
              {startRow}–{endRow} of {totalRows}
            </span>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              ›
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              »
            </button>

            {page === totalPages && nextCursor && (
              <button
                className="btn btn-outline-secondary ml-4"
                onClick={() => loadActivityLogs(nextCursor)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading more…' : 'Load more'}
              </button>
            )}

            {showEndOfLog && (
              <span className="text-xs text-secondary ml-4">
                End of activity log
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm">Rows per page</label>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', display: 'inline-block' }}
              value={pageSize}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setPageSize(val);
                setPage(1);
              }}
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>

            <button
              className="btn btn-outline-secondary ml-4"
              onClick={handleDownloadCsv}
              disabled={isDownloadingCsv || isLoading}
            >
              {isDownloadingCsv ? 'Preparing CSV…' : 'Download CSV'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
